import { ReactNode, useRef } from 'react'
import {
  Box,
  Divider,
  InputAdornment,
  MenuList,
  Popover,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material'
import VenueSearchIcon from '@mui/icons-material/Search'

type VenuePanelProps = {
  readonly anchorElement: HTMLElement | null
  readonly onClose: () => void
  /** Optional title row. Omitted by the single-venue mode, whose current-venue card already says what the panel is. */
  readonly title?: string
  readonly subtitle?: string
  readonly searchEnabled: boolean
  readonly searchQuery: string
  readonly onSearchQueryChange: (query: string) => void
  readonly searchPlaceholder?: string
  /** When non-null the list is replaced by this empty-state message. */
  readonly emptyMessage: string | null
  /** Optional action row rendered between the header and the list (e.g. the multisite Select all / Unselect all controls). */
  readonly headerActions?: ReactNode
  readonly children: ReactNode
}

/**
 * The dropdown panel shell shared by both switcher modes: a Popover with an
 * optional header (title + count caption), an optional type-to-filter search
 * field, an optional action row, and a scrollable MenuList.
 *
 * Deliberately a Popover + MenuList rather than a Menu (mirroring
 * `AppMenuFlyout`) as the MUI Menu captures printable keystrokes for its built-in
 * typeahead, which would steal focus/keystrokes from the search field.
 */
const VenuePanel = ({
  anchorElement,
  onClose,
  title,
  subtitle,
  searchEnabled,
  searchQuery,
  onSearchQueryChange,
  searchPlaceholder = 'Search venues',
  emptyMessage,
  headerActions,
  children,
}: VenuePanelProps) => {
  const listRef = useRef<HTMLUListElement>(null)

  // Only autofocus the search field when a fine pointer (mouse) is present.
  // On touch devices autofocusing pops the on-screen keyboard the moment the
  // switcher opens, covering the venue list before the user has chosen to
  // search.
  const autoFocusSearch = useMediaQuery('(pointer: fine)')

  // ArrowDown in the search field hands focus to the first enabled list item,
  // after which MenuList's own arrow-key handling takes over.
  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()

      const firstItem = listRef.current?.querySelector<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])'
      )
      firstItem?.focus()
    }
  }

  const hasTitleRow = Boolean(title || subtitle)
  const hasHeaderBlock = hasTitleRow || searchEnabled

  return (
    <Popover
      anchorEl={anchorElement}
      open={Boolean(anchorElement)}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      // Without this, the underlying MUI Modal sets `overflow: hidden` on the
      // body while open, which on Windows removes the always-present scrollbar
      // (`body { overflow-y: scroll }`) and shifts the layout. Scroll locking
      // isn't needed here: the popover anchors to the fixed topbar, so page
      // scrolling can't move it out of alignment.
      disableScrollLock
      slotProps={{
        paper: {
          sx: (theme) => ({
            marginTop: 1,
            // On mobile screens we want to have the switcher fill the screen
            // width, while on desktop we limit the width
            width: { xs: '100vw', sm: '300px' },
            maxWidth: { xs: '100vw', sm: 'calc(100vw - 16px)' },
            [theme.breakpoints.down('sm')]: {
              left: '0 !important',
              right: 0,
            },
          }),
        },
      }}
    >
      {hasHeaderBlock && (
        <Box
          paddingTop={hasTitleRow ? 2 : 1}
          paddingBottom={3}
          paddingLeft={1}
          paddingRight={1}
        >
          {hasTitleRow && (
            <Box
              display="flex"
              alignItems="baseline"
              justifyContent="space-between"
              gap={1}
            >
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {title}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                whiteSpace="nowrap"
              >
                {subtitle}
              </Typography>
            </Box>
          )}

          {searchEnabled && (
            // A raw MUI TextField rather than LoadedTextField: this is app
            // chrome, not a form field, and LoadedTextField's form-label layout
            // and Enter-blurs behaviour don't fit a compact filter input.
            <TextField
              value={searchQuery}
              onChange={(event) => onSearchQueryChange(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchPlaceholder}
              size="small"
              fullWidth
              autoFocus={autoFocusSearch}
              inputProps={{ 'aria-label': 'Search venues' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VenueSearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ marginTop: hasTitleRow ? 1.5 : 0 }}
            />
          )}
        </Box>
      )}

      {headerActions}

      {(hasHeaderBlock || Boolean(headerActions)) && <Divider />}

      {emptyMessage ? (
        <Box padding={2}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        // Scrollable list region so long venue lists never push the panel off
        // screen; the header and search stay pinned above it.
        <Box maxHeight="60vh" overflow="auto">
          <MenuList
            ref={listRef}
            // Without a search field to focus, put focus on the active (or
            // first) item so arrow keys work as soon as the panel opens.
            autoFocusItem={!searchEnabled}
            sx={{
              paddingTop: 0.5,
              paddingBottom: 0.5,
              '& .MuiMenuItem-root': {
                // Slightly tighten the padding to avoid too much whitespace
                paddingLeft: 1,
                paddingRight: 1,
                '&.Mui-selected': {
                  // Get rid of the default selected background colour, we rely
                  // on the bold text and checkmark icon to indicate its' active
                  backgroundColor: 'initial',
                },
              },
            }}
          >
            {children}
          </MenuList>
        </Box>
      )}
    </Popover>
  )
}

export default VenuePanel
